/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import {
  KatIcon, KatButton, KatAlert, KatSpinner,
} from '@amzn/katal-react';
import {
  selectPostById,
  threadDetailActions,
  submitPost,
  selectDraftStatus,
  selectDraftContent,
  selectDeleteDraftStatus,
  selectDraftPostErrors,
  selectDraftFieldErrors,
  deletePost,
} from 'src/redux/slices/threadDetailSlice';
import { useRouter } from 'next/router';
import {
  useTranslation,
  createProfileHref, serializeToPlaintext, serializeToHtml, formValidations,
  deserializeFromHtml, transformHtmlToReact, getElementsMetadata,
  customFetch,
} from 'src/utils';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import {
  DiscussionCard,
  DiscussionCardHeader,
  Avatar,
  CardType,
  Expander,
} from '@amzn/cuminuicomponents';
import {
  DraftPostType, OpPost, ReplyPost, ThreadDetailSubmitPostResponse, GetPostResponse,
} from 'src/types/DiscussionPost';
import PostDate from 'src/components/thread-detail/PostDate';
import ConfirmationModal, { ConfirmationType } from 'src/components/thread-detail/ConfirmationModal';
import RichTextEditor from 'src/components/RichTextEditor';
import { Descendant, Editor, Transforms } from 'slate';
import FetchStatus from 'src/types/FetchStatus';
import { Formik, Form } from 'formik';
import FormikField from 'src/components/FormikField';
import * as yup from 'yup';
import { getThreadDetail } from 'src/constants/pages';
import { ElementTypes, CustomEditor } from 'src/types/CustomSlateTypes';
import CommunityGuidelinesModal from 'src/components/thread-detail/CommunityGuidelinesModal';
import { DiscussionPostErrors } from 'src/constants/errorStrings';
import { AUTO_SAVE_DELAY } from 'src/constants/timers';
import PersonalDataModal from 'src/components/personal-data';
import { getPostApi } from 'src/constants/apiEndpoints';
import { selectMarketplaceId } from 'src/redux/slices/userInfo';
import { Group } from '../../types/Groups';

interface ReplyPostPanelProps {
  parentPostId: string;
  // eslint-disable-next-line react/require-default-props
  postId?: string;
  type: DraftPostType;
  customEditor: CustomEditor;
  opPostGroup?: Group
}

const initialValues = {
  content: [
    {
      type: ElementTypes.paragraph,
      children: [
        { text: '' },
      ],
    },
  ] as Descendant[],
};

const ReplyPostPanel: React.FC<ReplyPostPanelProps> = ({
  parentPostId,
  type,
  postId,
  customEditor,
  opPostGroup,
}) => {
  const { t } = useTranslation();
  const { push, query } = useRouter();
  const { threadId } = query as { threadId: string, postId: string };
  const dispatch = useAppDispatch();
  const draftPostErrors = useAppSelector(selectDraftPostErrors);
  const draftFieldErrors = useAppSelector(selectDraftFieldErrors);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [confirmationType, setConfirmationType] = useState<ConfirmationType | undefined>(undefined);
  const parentPost = useAppSelector((state: AppState) => selectPostById(state, parentPostId));
  const draftStatus = useAppSelector(selectDraftStatus);
  const savedDraftContent = useAppSelector(selectDraftContent);
  const deleteDraftStatus = useAppSelector(selectDeleteDraftStatus);
  const marketplaceId = useAppSelector(selectMarketplaceId);
  const [postDetailsStatus, setPostDetailsStatus] = useState(FetchStatus.idle);
  const [postDetails, setPostDetails] = useState<OpPost | ReplyPost | undefined>(
    parentPost && parentPost.postDetails,
  );
  const isInternalUser = useAppSelector(({ userInfo }: AppState) => userInfo.isInternal);

  useEffect(() => {
    // setting post detail states if parentPostId is updated
    setPostDetails(parentPost && parentPost.postDetails);
    if ([FetchStatus.complete, FetchStatus.failed].includes(postDetailsStatus)) {
      setPostDetailsStatus(FetchStatus.idle);
    }
  }, [parentPostId]);

  if (!postDetails) {
    /**
     * Fetching post if replyto post is not found in the store
     */
    const getPostDetails = async () => {
      setPostDetailsStatus(FetchStatus.loading);
      try {
        const response: GetPostResponse = await customFetch(
          getPostApi(threadId, parentPostId),
          {
            method: 'POST',
            body: JSON.stringify({ marketplaceId }),
          },
        );
        setPostDetails(response.post);
        setPostDetailsStatus(FetchStatus.complete);
      } catch {
        setPostDetailsStatus(FetchStatus.failed);
      }
    };
    if (postDetailsStatus === FetchStatus.failed) {
      return (
        <KatAlert
          data-testid="reply-post-fetch-failure"
          variant="warning"
          header={t('request_threadDetailsFetchReplyFailureHeader')}
          description={t('reload_page')}
          className="mt-4"
          persistent
        />
      );
    }
    if (postDetailsStatus === FetchStatus.idle) {
      getPostDetails();
    }
    return <KatSpinner />;
  }
  const {
    userId: postUserId,
    username: postUserName,
    avatarSrc,
    isDeleted,
    title,
    createdAt,
    updatedAt,
    content: replyContentData,
    contentMetadata,
    replyToUsername,
    updatedByModerator,
    isMigrated,
  } = postDetails as OpPost & ReplyPost;
  const linkToProfile = postUserId ? createProfileHref(postUserId) : undefined;
  const headerLabel = type === DraftPostType.edit ? t('replyPostPanel_edit_header') : t('replyPostPanel_reply_header');
  const submitActionLabel = type === DraftPostType.edit ? t('replyPostPanel_edit_submit_label') : t('replyPostPanel_reply_submit_label');
  const replyContentField = 'content';
  let debounceTimer: ReturnType<typeof setTimeout>;

  const cancelHandler = (replyContent: Descendant[]) => {
    if (!serializeToPlaintext(replyContent).trim()) {
      if (postId && type === DraftPostType.reply) {
        dispatch(deletePost({ threadId, postId }));
      } else {
        dispatch(threadDetailActions.setDraftPost(undefined));
      }
    } else {
      setConfirmationType(ConfirmationType.cancelReply);
    }
    dispatch(threadDetailActions.resetDraftStatus());
  };

  const getContent = () => {
    if (isDeleted) {
      const deletedMessage = updatedByModerator
        ? t('discussionCard_postDeleted_byModerator')
        : t('discussionCard_postDeleted');
      return (
        <span className="bg-kat-seattle p-1.5 before:content-['['] after:content-[']']">
          {deletedMessage}
        </span>
      );
    }
    return (
      <Expander>
        <span
          data-testid="post-content"
          className={`break-words images:max-w-full ${isMigrated ? 'discourse-forums' : ''}`}
        >
          {transformHtmlToReact({
            html: replyContentData,
            metadata: contentMetadata,
          })}
        </span>
      </Expander>
    );
  };

  return (
    <div className="sticky top-5 mt-5">
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape({
          content: formValidations.content(isInternalUser),
        })}
        initialStatus={{ hasChanged: false }}
        validateOnBlur={false}
        onSubmit={(values) => {
          clearTimeout(debounceTimer);
          dispatch(submitPost({
            threadId,
            inReplyPostId: parentPostId,
            id: postId,
            draft: false,
            content: serializeToHtml(values.content),
            contentMetadata: getElementsMetadata(values.content),
          })).then(({ payload, type: responseType }) => {
            const replyPost = payload as ThreadDetailSubmitPostResponse;
            if (responseType === submitPost.fulfilled?.type) {
              dispatch(
                threadDetailActions.setFocusedPostId(replyPost.post.id),
              );
              push(
                {
                  pathname: getThreadDetail(threadId),
                  query: { postId: replyPost.post.id },
                },
                undefined,
                {
                  shallow: true,
                },
              );
            }
          });
        }}
      >
        {
          ({
            setFieldValue, values, setStatus, status, setFieldError, isSubmitting,
          }) => {
            const { content } = values;
            useEffect(() => {
              if (
                type === DraftPostType.reply
                && serializeToHtml(content).length <= 32000
                && status.hasChanged
              ) {
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                  dispatch(submitPost({
                    threadId,
                    inReplyPostId: parentPostId,
                    id: postId,
                    draft: true,
                    content: serializeToHtml(content),
                    contentMetadata: getElementsMetadata(content),
                  }));
                }, AUTO_SAVE_DELAY);
              }
              return () => clearTimeout(debounceTimer);
            }, [content]);

            useEffect(() => {
              if (draftFieldErrors && draftFieldErrors.length > 0) {
                draftFieldErrors.forEach((error) => {
                  setFieldError(error.errorField, error.errors);
                });
              }
            }, [draftFieldErrors]);

            useEffect(() => {
              let newContent = initialValues.content;
              if (savedDraftContent!.content) {
                newContent = deserializeFromHtml({
                  html: savedDraftContent.content,
                  metadata: savedDraftContent.contentMetadata,
                }) as Descendant[];
              }
              Editor.withoutNormalizing(customEditor, () => {
                Transforms.deselect(customEditor);
                Transforms.removeNodes(customEditor, {
                  at: {
                    anchor: Editor.start(customEditor, []),
                    focus: Editor.end(customEditor, []),
                  },
                });
                Transforms.insertNodes(customEditor, newContent);
                // eslint-disable-next-line no-param-reassign
                customEditor.history = { redos: [], undos: [] };
              });
              setFieldValue('content', newContent, true);
            }, [type, savedDraftContent.content]);

            return (
              <Form>
                <div className="mb-4 flex flex-col justify-between">
                  <div className="flex justify-between">
                    <h2 data-testid="reply-panel-header">{headerLabel}</h2>
                    <button
                      data-testid="reply-panel-close"
                      aria-label="close reply"
                      slot="trigger"
                      type="button"
                      className="cursor-pointer border-0 bg-transparent p-0"
                      onClick={() => cancelHandler(content)}
                    >
                      <KatIcon name="close" size="small" />
                    </button>
                  </div>
                  <div className="flex">
                    <button
                      data-testid="guidelines-button"
                      type="button"
                      className="cursor-pointer border-0 bg-transparent p-0 text-kat-eastern"
                      onClick={() => { setShowGuidelinesModal(true); }}
                    >
                      {t('discussionGuidelinesReply')}
                      <KatIcon className="ltr:ml-1 rtl:mr-1" name="hint" size="tiny" />
                    </button>
                  </div>
                </div>
                <div className="max-h-[90vh] overflow-y-auto px-0.5 pb-10">
                  <DiscussionCard
                    className="border border-solid border-kat-seattle bg-kat-white"
                    cardType={CardType.reply}
                    header={(
                      <DiscussionCardHeader
                        replyingToAuthor={replyToUsername}
                        title={title}
                        authoredByHref={linkToProfile}
                        authoredBy={postUserName}
                        datePosted={(
                          <PostDate
                            createdAt={createdAt}
                            updatedAt={updatedAt}
                            updatedByModerator={!!updatedByModerator}
                          />
                      )}
                        avatar={(
                          <Avatar
                            href={linkToProfile}
                            src={avatarSrc}
                          />
                      )}
                      />
                    )}
                    content={getContent()}
                  />
                  <FormikField
                    customEditor={customEditor}
                    data-testid="reply-post-textarea"
                    component={RichTextEditor}
                    fast
                    name={replyContentField}
                    placeholder={t('replyContent_placeholder')}
                    onChange={(value: Descendant[]) => {
                      setStatus({ hasChanged: true });
                      setFieldValue(replyContentField, value, true);
                    }}
                    selectedGroup={opPostGroup}
                  />
                  <PersonalDataModal />
                  {
                    draftStatus === FetchStatus.loading && !isSubmitting && <div data-testid="reply-auto-save" className="mt-1.5 text-xs italic text-kat-tarpon">{t('threadDetail_autoSave')}</div>
                  }
                  <div className="my-5 flex flex-row-reverse space-x-4 ltr:space-x-reverse">
                    <KatButton
                      variant="primary"
                      data-testid="reply-panel-submit"
                      loading={[draftStatus, deleteDraftStatus].includes(FetchStatus.loading)}
                      type="submit"
                    >
                      <KatIcon slot="icon" name="reply" size="tiny" />
                      {submitActionLabel}
                    </KatButton>
                    <KatButton
                      data-testid="reply-panel-cancel"
                      variant="secondary"
                      label={t('cancel')}
                      disabled={[draftStatus, deleteDraftStatus].includes(FetchStatus.loading)}
                      onClick={() => cancelHandler(content)}
                    />
                  </div>
                </div>
                {
                  draftPostErrors && draftPostErrors
                    .map((draftPostError) => (
                      <KatAlert
                        key={draftPostError}
                        data-testid="reply-panel-failure-msg"
                        persistent
                        variant="warning"
                        className="mb-1"
                        header={t(DiscussionPostErrors[draftPostError].header)}
                        description={t(DiscussionPostErrors[draftPostError].description)}
                      />
                    ))
                }
                {
                confirmationType
                && (
                <ConfirmationModal
                  postId={type === DraftPostType.reply ? postId : undefined}
                  threadId={threadId}
                  type={confirmationType}
                  onClose={() => setConfirmationType(undefined)}
                />
                )
              }
                <CommunityGuidelinesModal
                  visible={showGuidelinesModal}
                  title={t('guidelines_tooltip_modal_reply_title')}
                  onClose={() => { setShowGuidelinesModal(false); }}
                />
              </Form>
            );
          }
        }
      </Formik>
    </div>
  );
};

ReplyPostPanel.defaultProps = {
  opPostGroup: undefined,
};

export default ReplyPostPanel;
